"use client"

import Link from "next/link"
import Image from "next/image"
import { useState, useEffect } from "react"
import { usePathname, useRouter } from "next/navigation"
import { Menu, X, Monitor, Smartphone, ShoppingCart, Zap, Palette, Code, PenTool, ArrowRight, FileText, Briefcase, Mail, Sparkles } from "lucide-react"
import { Button } from "@/components/ui/button"
import { isValidLocale, type Locale } from "@/lib/i18n"
import { getTranslations } from "@/lib/translations"
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

// Helper function to get menu data based on locale
function getMenuData(locale: Locale) {
  // Ensure locale is valid
  const safeLocale: Locale = isValidLocale(locale) ? locale : 'tr'
  
  try {
    const t = getTranslations(safeLocale)
    
    // Safety check: if translations don't have header, return default structure
    if (!t || !t.header) {
      console.error('Translations not loaded properly for locale:', safeLocale, 't:', t)
      // Return a minimal structure to prevent crash
      return {
        hizmetlerMenu: { title: 'Services', categories: [] },
        kurumsalMenu: { title: 'Corporate', items: [] }
      }
    }
    
    const iconMap: Record<string, any> = {
    "Web Tasarım": Palette,
    "Web Design": Palette,
    "Web Yazılım": Code,
    "Web Development": Code,
    "UI & UX Tasarım": PenTool,
    "UI & UX Design": PenTool,
    "Mobil Uygulama": Smartphone,
    "Mobile App": Smartphone,
    "Masaüstü Uygulama": Monitor,
    "Desktop App": Monitor,
    "E-ticaret": ShoppingCart,
    "E-Commerce": ShoppingCart,
    "Shopify": ShoppingCart,
    "WooCommerce": ShoppingCart,
    "Otomasyon": Zap,
    "Automation": Zap,
    "AI Servisler": Zap,
    "AI Services": Zap,
  }

  return {
    hizmetlerMenu: {
      title: t.header.services,
      categories: [
        {
          title: t.header.serviceCategories.webDevelopment,
          items: [
            { 
              href: `/${safeLocale === 'tr' ? 'hizmetler/web-tasarim' : 'services/web-design'}`, 
              label: t.header.serviceItems.webDesign.label,
              description: t.header.serviceItems.webDesign.description,
              icon: iconMap[t.header.serviceItems.webDesign.label] || Palette,
            },
            { 
              href: `/${safeLocale === 'tr' ? 'hizmetler/web-yazilim' : 'services/web-development'}`,
              label: t.header.serviceItems.webDevelopment.label,
              description: t.header.serviceItems.webDevelopment.description,
              icon: iconMap[t.header.serviceItems.webDevelopment.label] || Code,
            },
            {
              href: `/${safeLocale === 'tr' ? 'hizmetler/ui-ux-tasarim' : 'services/ui-ux-design'}`,
              label: t.header.serviceItems.uiUx.label,
              description: t.header.serviceItems.uiUx.description,
              icon: iconMap[t.header.serviceItems.uiUx.label] || PenTool,
            },
          ],
        },
        {
          title: t.header.serviceCategories.mobileDesktop,
          items: [
            { 
              href: `/${safeLocale === 'tr' ? 'hizmetler/mobil-uygulama' : 'services/mobile-app'}`, 
              label: t.header.serviceItems.mobileApp.label,
              description: t.header.serviceItems.mobileApp.description,
              icon: iconMap[t.header.serviceItems.mobileApp.label] || Smartphone,
            },
            { 
              href: `/${safeLocale === 'tr' ? 'hizmetler/masaustu-uygulama' : 'services/desktop-app'}`, 
              label: t.header.serviceItems.desktopApp.label,
              description: t.header.serviceItems.desktopApp.description,
              icon: iconMap[t.header.serviceItems.desktopApp.label] || Monitor,
            },
          ],
        },
        {
          title: t.header.serviceCategories.eCommerce,
          items: [
            { 
              href: `/${safeLocale === 'tr' ? 'hizmetler/e-ticaret' : 'services/e-commerce'}`, 
              label: t.header.serviceItems.eCommerce.label,
              description: t.header.serviceItems.eCommerce.description,
              icon: iconMap[t.header.serviceItems.eCommerce.label] || ShoppingCart,
            },
            { 
              href: `/${safeLocale === 'tr' ? 'hizmetler/shopify' : 'services/shopify'}`, 
              label: t.header.serviceItems.shopify.label,
              description: t.header.serviceItems.shopify.description,
              icon: iconMap[t.header.serviceItems.shopify.label] || ShoppingCart,
            },
            { 
              href: `/${safeLocale === 'tr' ? 'hizmetler/woocommerce' : 'services/woocommerce'}`, 
              label: t.header.serviceItems.woocommerce.label,
              description: t.header.serviceItems.woocommerce.description,
              icon: iconMap[t.header.serviceItems.woocommerce.label] || ShoppingCart,
            },
          ],
        },
        {
          title: t.header.serviceCategories.automationAI,
          items: [
            { 
              href: `/${safeLocale === 'tr' ? 'hizmetler/otomasyon' : 'services/automation'}`,
              label: t.header.serviceItems.automation.label,
              description: t.header.serviceItems.automation.description,
              icon: iconMap[t.header.serviceItems.automation.label] || Zap,
            },
            { 
              href: `/${safeLocale === 'tr' ? 'hizmetler/ai-servisler' : 'services/ai-services'}`, 
              label: t.header.serviceItems.aiServices.label,
              description: t.header.serviceItems.aiServices.description,
              icon: iconMap[t.header.serviceItems.aiServices.label] || Zap,
            },
          ],
        },
      ],
    },
    kurumsalMenu: {
      title: t.header.corporate,
      items: [
        { href: `/${safeLocale === 'tr' ? 'hakkimizda' : 'about'}`, label: t.header.corporateItems.about },
        { href: `/${safeLocale === 'tr' ? 'kurumsal-kimlik' : 'corporate-identity'}`, label: t.header.corporateItems.corporateIdentity },
        { href: '/laki', label: t.header.corporateItems.laki },
        { href: `/${safeLocale === 'tr' ? 'kariyer' : 'career'}`, label: t.header.corporateItems.career },
      ],
    },
  }
  } catch (error) {
    console.error('Error in getMenuData:', error)
    // Return a minimal structure to prevent crash
    return {
      hizmetlerMenu: { title: 'Services', categories: [] },
      kurumsalMenu: { title: 'Corporate', items: [] }
    }
  }
}

export function Header() {
  const pathname = usePathname()
  const router = useRouter()
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [openCategories, setOpenCategories] = useState<Set<string>>(new Set())

  // Get initial locale from pathname (server-side safe)
  const getInitialLocale = (): Locale => {
    const segments = pathname.split('/').filter(Boolean)
    const firstSegment = segments[0]
    // Login, admin, and api routes don't have locale prefix
    if (firstSegment === 'login' || firstSegment === 'admin' || firstSegment === 'api') {
      return 'tr' // Default for server-side
    }
    return isValidLocale(firstSegment) ? firstSegment : 'tr'
  }

  const [currentLocale, setCurrentLocale] = useState<Locale>(getInitialLocale())

  // Update locale on mount and pathname change (client-side only)
  useEffect(() => {
    const segments = pathname.split('/').filter(Boolean)
    const firstSegment = segments[0]
    
    if (firstSegment === 'login' || firstSegment === 'admin' || firstSegment === 'api') {
      // For these routes, check localStorage
      if (typeof window !== 'undefined') {
        const savedLocale = localStorage.getItem('customer-panel-locale')
        if (savedLocale && isValidLocale(savedLocale)) {
          setCurrentLocale(savedLocale as Locale)
          return
        }
      }
      setCurrentLocale('tr')
    } else {
      // For locale-prefixed routes, use the locale from pathname
      const locale = isValidLocale(firstSegment) ? firstSegment : 'tr'
      setCurrentLocale(locale)
    }
  }, [pathname])
  
  // Ensure locale is valid and get translations safely
  const safeLocale: Locale = isValidLocale(currentLocale) ? currentLocale : 'tr'
  const t = getTranslations(safeLocale)
  const { hizmetlerMenu, kurumsalMenu } = getMenuData(safeLocale)

  // Get path without locale
  const getPathWithoutLocale = (): string => {
    const segments = pathname.split('/').filter(Boolean)
    // Login, admin, and api routes don't have locale prefix
    if (segments[0] === 'login' || segments[0] === 'admin' || segments[0] === 'api') {
      return pathname
    }
    if (isValidLocale(segments[0])) {
      return '/' + segments.slice(1).join('/')
    }
    return pathname
  }

  // Slug mapping for Turkish to English and vice versa
  const slugMapping: Record<string, { tr: string; en: string }> = {
    // Services
    'hizmetler': { tr: 'hizmetler', en: 'services' },
    'services': { tr: 'hizmetler', en: 'services' },
    'web-tasarim': { tr: 'web-tasarim', en: 'web-design' },
    'web-design': { tr: 'web-tasarim', en: 'web-design' },
    'web-yazilim': { tr: 'web-yazilim', en: 'web-development' },
    'web-development': { tr: 'web-yazilim', en: 'web-development' },
    'ui-ux-tasarim': { tr: 'ui-ux-tasarim', en: 'ui-ux-design' },
    'ui-ux-design': { tr: 'ui-ux-tasarim', en: 'ui-ux-design' },
    'mobil-uygulama': { tr: 'mobil-uygulama', en: 'mobile-app' },
    'mobile-app': { tr: 'mobil-uygulama', en: 'mobile-app' },
    'masaustu-uygulama': { tr: 'masaustu-uygulama', en: 'desktop-app' },
    'desktop-app': { tr: 'masaustu-uygulama', en: 'desktop-app' },
    'e-ticaret': { tr: 'e-ticaret', en: 'e-commerce' },
    'e-commerce': { tr: 'e-ticaret', en: 'e-commerce' },
    'shopify': { tr: 'shopify', en: 'shopify' },
    'woocommerce': { tr: 'woocommerce', en: 'woocommerce' },
    'otomasyon': { tr: 'otomasyon', en: 'automation' },
    'automation': { tr: 'otomasyon', en: 'automation' },
    'ai-servisler': { tr: 'ai-servisler', en: 'ai-services' },
    'ai-services': { tr: 'ai-servisler', en: 'ai-services' },
    // Pages
    'hakkimizda': { tr: 'hakkimizda', en: 'about' },
    'about': { tr: 'hakkimizda', en: 'about' },
    'kurumsal-kimlik': { tr: 'kurumsal-kimlik', en: 'corporate-identity' },
    'corporate-identity': { tr: 'kurumsal-kimlik', en: 'corporate-identity' },
    'laki': { tr: 'laki', en: 'laki' },
    'iletisim': { tr: 'iletisim', en: 'contact' },
    'contact': { tr: 'iletisim', en: 'contact' },
    'teklif-al': { tr: 'teklif-al', en: 'quote-request' },
    'quote-request': { tr: 'teklif-al', en: 'quote-request' },
    'hayalini-anlat': { tr: 'hayalini-anlat', en: 'tell-your-dream' },
    'tell-your-dream': { tr: 'hayalini-anlat', en: 'tell-your-dream' },
    'kariyer': { tr: 'kariyer', en: 'career' },
    'career': { tr: 'kariyer', en: 'career' },
    'basvuru': { tr: 'basvuru', en: 'application' },
    'application': { tr: 'basvuru', en: 'application' },
    'referanslar': { tr: 'referanslar', en: 'references' },
    'references': { tr: 'referanslar', en: 'references' },
    'gizlilik': { tr: 'gizlilik', en: 'privacy' },
    'privacy': { tr: 'gizlilik', en: 'privacy' },
    'cerez-politikasi': { tr: 'cerez-politikasi', en: 'cookie-policy' },
    'cookie-policy': { tr: 'cerez-politikasi', en: 'cookie-policy' },
    'kullanim-kosullari': { tr: 'kullanim-kosullari', en: 'terms-of-use' },
    'terms-of-use': { tr: 'kullanim-kosullari', en: 'terms-of-use' },
    'kvkk': { tr: 'kvkk', en: 'data-protection-law' },
    'data-protection-law': { tr: 'kvkk', en: 'data-protection-law' },
  }

  // Convert slug from one locale to another
  const convertSlug = (slug: string, fromLocale: Locale, toLocale: Locale): string => {
    if (fromLocale === toLocale) return slug
    
    // Check if slug exists in mapping
    const mapping = slugMapping[slug]
    if (mapping) {
      return mapping[toLocale]
    }
    
    // If not found, return original slug (for dynamic routes like blog posts, references)
    return slug
  }

  // Switch language with slug conversion
  const switchLanguage = (newLocale: Locale) => {
    // Remember locale for pages without a locale prefix (login, admin)
    if (typeof window !== 'undefined') {
      localStorage.setItem('customer-panel-locale', newLocale)
    }
    
    const pathWithoutLocale = getPathWithoutLocale()
    const segments = pathWithoutLocale.split('/').filter(Boolean)
    
    // If we're on login, admin, or api routes, don't change locale
    if (segments[0] === 'login' || segments[0] === 'admin' || segments[0] === 'api') {
      // These routes don't have locale prefix, just stay on the same page
      return
    }
    
    // If it's just the root path, redirect to homepage
    if (pathWithoutLocale === '/' || pathWithoutLocale === '') {
      router.push(`/${newLocale}`)
      return
    }
    
    // Check if we're on a blog or reference detail page
    // If so, redirect to the listing page in the new locale instead
    if (segments.length >= 2) {
      const firstSegment = segments[0]
      
      // Check if it's a blog detail page
      if (firstSegment === 'blog') {
        router.push(`/${newLocale}/blog`)
        return
      }
      
      // Check if it's a reference detail page
      if (firstSegment === 'referanslar' || firstSegment === 'references') {
        const referencesPath = newLocale === 'tr' ? 'referanslar' : 'references'
        router.push(`/${newLocale}/${referencesPath}`)
        return
      }
    }
    
    // Convert each segment
    const convertedSegments = segments.map(segment => {
      return convertSlug(segment, currentLocale, newLocale)
    })
    
    // Build new path
    const newPath = `/${newLocale}/${convertedSegments.join('/')}`
    router.push(newPath)
  }

  // Toggle category accordion
  const toggleCategory = (categoryTitle: string) => {
    setOpenCategories(prev => {
      const newSet = new Set(prev)
      if (newSet.has(categoryTitle)) {
        newSet.delete(categoryTitle)
      } else {
        newSet.add(categoryTitle)
      }
      return newSet
    })
  }

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      // Save current scroll position
      const scrollY = window.scrollY
      
      // Prevent body scroll
      document.body.style.position = "fixed"
      document.body.style.top = `-${scrollY}px`
      document.body.style.width = "100%"
      document.body.style.overflow = "hidden"
      
      return () => {
        // Restore scroll position
        document.body.style.position = ""
        document.body.style.top = ""
        document.body.style.width = ""
        document.body.style.overflow = ""
        window.scrollTo(0, scrollY)
      }
    } else {
      // Close all categories when menu closes
      setOpenCategories(new Set())
    }
  }, [mobileMenuOpen])

  return (
    <header className="fixed top-0 left-0 right-0 z-[60] w-full bg-[#feffff] shadow-sm" suppressHydrationWarning>
      {/* Top Bar - Desktop: Slogan + Language | Mobile: Sadece Slogan */}
      <div className="border-b border-slate-200 bg-[#fbfcfd]" suppressHydrationWarning>
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 flex h-9 items-center justify-between">
          {/* Sol: Slogan - Her iki platformda da */}
          <div className="text-[10px] sm:text-xs text-slate-600 font-mono truncate flex-1 sm:flex-none" suppressHydrationWarning>
            {currentLocale === 'tr' 
              ? 'Dijital fikirler, gerçek çözümler.'
              : 'Digital ideas, real solutions.'}
          </div>
          
          {/* Sağ: Dil Seçici ve Müşteri Paneli */}
          <div className="flex items-center gap-2 lg:gap-3" suppressHydrationWarning>
            {/* Dil Seçici - Desktop: Bayrak + Text, Mobile: Sadece Bayrak */}
            <Select value={currentLocale} onValueChange={(value) => switchLanguage(value as Locale)}>
              <SelectTrigger 
                className="h-7 min-w-[32px] lg:min-w-[60px] border-0 bg-transparent text-slate-700 hover:bg-slate-100 px-1.5 lg:px-2 flex items-center justify-center lg:gap-1.5" 
                aria-label={currentLocale === 'tr' ? 'Dil seçici: Türkçe' : 'Language selector: English'}
                suppressHydrationWarning
              >
                <SelectValue suppressHydrationWarning>
                  <div className="flex items-center gap-1.5">
                    <Image
                      src={currentLocale === "tr" ? "/turkey.png" : "/united-states.png"}
                      alt={currentLocale === "tr" ? "Türkçe" : "English"}
                      width={20}
                      height={15}
                      className="w-4 h-4 lg:w-4 lg:h-4 object-contain"
                    />
                    <span className="hidden lg:inline text-xs font-medium">{currentLocale.toUpperCase()}</span>
                  </div>
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="z-[70]">
                <SelectItem value="tr" className="data-[highlighted]:bg-slate-100 data-[highlighted]:text-slate-900">
                  <div className="flex items-center gap-2">
                    <Image
                      src="/turkey.png"
                      alt="Türkçe"
                      width={24}
                      height={18}
                      className="w-5 h-5 object-contain"
                    />
                    <span className="font-medium text-sm">Türkçe</span>
                  </div>
                </SelectItem>
                <SelectItem value="en" className="data-[highlighted]:bg-slate-100 data-[highlighted]:text-slate-900">
                  <div className="flex items-center gap-2">
                    <Image
                      src="/united-states.png"
                      alt="English"
                      width={24}
                      height={18}
                      className="w-5 h-5 object-contain"
                    />
                    <span className="font-medium text-sm">English</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>

          </div>
        </div>
      </div>

      {/* Main Header - Coda.co Style */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 flex h-16 items-center justify-between bg-[#feffff]" suppressHydrationWarning>
        {/* Logo */}
        <Link href={`/${currentLocale}`} className="flex items-center space-x-2 group max-w-[192px]" suppressHydrationWarning>
          <Image
            src="/LAKVERSE-black.png"
            alt="LAKVERSE"
            width={192}
            height={64}
            className="h-8 sm:h-10 md:h-12 w-auto"
            priority
            quality={70}
            sizes="(max-width: 640px) 128px, (max-width: 768px) 160px, 192px"
          />
        </Link>

        {/* Desktop Navigation - Center */}
        <div className="hidden lg:flex items-center space-x-1 flex-1 justify-center">
          <nav className="flex items-center">
            <NavigationMenu viewport={false}>
              <NavigationMenuList className="space-x-1">
                {/* Hizmetler Mega Menu */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="h-10 text-slate-900 bg-transparent hover:!bg-slate-50 hover:!text-slate-900 focus:!bg-transparent focus:!text-slate-900 data-[state=open]:!bg-slate-50 data-[state=open]:!text-slate-900 rounded-lg px-4 text-sm font-medium font-mono">
                    {t.header.services}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-[1000px] p-8 bg-[#feffff] shadow-xl border border-slate-200 rounded-lg">
                      <div className="grid grid-cols-4 gap-8">
                        {hizmetlerMenu.categories.map((category, index) => (
                          <div key={index} className="space-y-4">
                            <h3 className="text-xs font-semibold text-slate-500 uppercase tracking-wider font-mono">
                              {category.title}
                            </h3>
                            <ul className="space-y-4">
                              {category.items.map((item) => {
                                const Icon = item.icon
                                return (
                                  <li key={item.href}>
                                    <NavigationMenuLink asChild className="hover:!bg-transparent hover:!text-slate-900 focus:!bg-transparent focus:!text-slate-900">
                                      <Link
                                        href={`/${currentLocale}${item.href}`}
                                        className="group block space-y-1"
                                      >
                                        <div className="flex items-start gap-3">
                                          <div className="mt-1">
                                            <Icon className="h-5 w-5 text-slate-700 group-hover:text-slate-900 transition-colors" />
                                          </div>
                                          <div className="flex-1">
                                            <div className="text-sm font-medium text-slate-900 group-hover:text-slate-900 transition-colors font-mono">
                                              {item.label}
                                            </div>
                                            <div className="text-xs text-slate-500 mt-1 font-mono">
                                              {item.description}
                                            </div>
                                          </div>
                                        </div>
                                      </Link>
                                    </NavigationMenuLink>
                                  </li>
                                )
                              })}
                            </ul>
                          </div>
                        ))}
                      </div>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* Kurumsal Menu */}
                <NavigationMenuItem>
                  <NavigationMenuTrigger className="h-10 text-slate-900 bg-transparent hover:!bg-slate-50 hover:!text-slate-900 focus:!bg-transparent focus:!text-slate-900 data-[state=open]:!bg-slate-50 data-[state=open]:!text-slate-900 rounded-lg px-4 text-sm font-medium font-mono">
                    {t.header.corporate}
                  </NavigationMenuTrigger>
                  <NavigationMenuContent>
                    <div className="w-[240px] p-4 bg-[#feffff] shadow-xl border border-slate-200 rounded-lg">
                      <ul className="space-y-2">
                        {kurumsalMenu.items.map((item) => (
                          <li key={item.href}>
                            <NavigationMenuLink asChild className="hover:!bg-transparent hover:!text-slate-900 focus:!bg-transparent focus:!text-slate-900">
                              <Link
                                href={`/${currentLocale}${item.href}`}
                                className="block text-sm font-medium text-slate-700 hover:text-slate-900 transition-colors rounded-lg px-3 py-2 hover:bg-slate-50"
                              >
                                {item.label}
                              </Link>
                            </NavigationMenuLink>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </NavigationMenuContent>
                </NavigationMenuItem>

                {/* Referanslar */}
                <NavigationMenuItem>
                  <NavigationMenuLink asChild className="hover:!bg-transparent hover:!text-slate-900 focus:!bg-transparent focus:!text-slate-900">
                    <Link
                      href={`/${currentLocale}/${currentLocale === 'tr' ? 'referanslar' : 'references'}`}
                      className="group relative inline-flex h-10 items-center justify-center rounded-lg px-4 text-sm font-medium text-slate-900 hover:bg-slate-50 transition-colors"
                    >
                      {t.header.references}
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                {/* Blog */}
                <NavigationMenuItem>
                  <NavigationMenuLink asChild className="hover:!bg-transparent hover:!text-slate-900 focus:!bg-transparent focus:!text-slate-900">
                    <Link
                      href={`/${currentLocale}/blog`}
                      className="group relative inline-flex h-10 items-center justify-center rounded-lg px-4 text-sm font-medium text-slate-900 hover:bg-slate-50 transition-colors"
                    >
                      {t.header.blog}
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>

                {/* İletişim */}
                <NavigationMenuItem>
                  <NavigationMenuLink asChild className="hover:!bg-transparent hover:!text-slate-900 focus:!bg-transparent focus:!text-slate-900">
                    <Link
                      href={`/${currentLocale}/${currentLocale === 'tr' ? 'iletisim' : 'contact'}`}
                      className="group relative inline-flex h-10 items-center justify-center rounded-lg px-4 text-sm font-medium text-slate-900 hover:bg-slate-50 transition-colors"
                    >
                      {t.header.contact}
                    </Link>
                  </NavigationMenuLink>
                </NavigationMenuItem>
              </NavigationMenuList>
            </NavigationMenu>
          </nav>
        </div>

        {/* Right Side - CTA Buttons */}
        <div className="hidden lg:flex items-center space-x-4" suppressHydrationWarning>
          {/* Hayalini Anlat Butonu - Gradient */}
          <Button 
            asChild
            className="bg-[#034AA6] hover:bg-[#034AA6]/90 text-white transition-all duration-200 rounded-lg h-9 px-4 flex items-center gap-2"
          >
            <Link href={`/${currentLocale}/hayalini-anlat`}>
              <Sparkles className="h-4 w-4" />
              <span className="hidden sm:inline">{t.dreamAssistant?.title || (currentLocale === 'tr' ? 'Hayalini Anlat' : 'Tell Your Dream')}</span>
              <span className="sm:hidden">{currentLocale === 'tr' ? 'Hayal' : 'Dream'}</span>
            </Link>
          </Button>

          {/* Teklif Al Butonu - Siyah */}
          <Button asChild className="bg-slate-900 text-white hover:bg-slate-800 transition-all duration-200 rounded-lg h-9 px-4">
            <Link href={`/${currentLocale}/${currentLocale === 'tr' ? 'teklif-al' : 'quote-request'}`}>{t.header.getQuote}</Link>
          </Button>
        </div>

        {/* Mobile Quick Actions - Before Menu Button */}
        <div className="lg:hidden flex items-center gap-2">
          {/* Hayalini Anlat - Kompakt İkon Butonu */}
          <Button
            asChild
            size="icon"
            className="h-9 w-9 p-0 bg-[#034AA6] hover:bg-[#034AA6]/90 active:bg-[#034AA6]/80 text-white rounded-lg shadow-sm transition-all"
            title={t.dreamAssistant?.title || (currentLocale === 'tr' ? 'Hayalini Anlat' : 'Tell Your Dream')}
          >
            <Link href={`/${currentLocale}/hayalini-anlat`}>
              <Sparkles className="h-4 w-4" />
            </Link>
          </Button>

          {/* Teklif Al - Kompakt İkon Butonu */}
          <Button
            asChild
            size="icon"
            className="h-9 w-9 p-0 bg-slate-900 hover:bg-slate-800 active:bg-slate-950 text-white rounded-lg shadow-sm transition-all"
            title={t.header.getQuote}
          >
            <Link href={`/${currentLocale}/${currentLocale === 'tr' ? 'teklif-al' : 'quote-request'}`}>
              <ArrowRight className="h-4 w-4" />
            </Link>
          </Button>

          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            className="h-9 w-9 rounded-lg hover:bg-slate-50 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label={mobileMenuOpen ? t.header.menuClose : t.header.menuOpen}
            suppressHydrationWarning
          >
            {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <>
          {/* Backdrop - No blur, just slight darkening */}
          <div 
            className="fixed inset-0 bg-black/10 z-[65] lg:hidden animate-in fade-in duration-200"
            onClick={() => setMobileMenuOpen(false)}
            aria-hidden="true"
          />
          
          {/* Mobile Menu */}
          <div className="lg:hidden fixed top-0 left-0 right-0 bottom-0 bg-[#feffff] z-[66] overflow-hidden animate-in slide-in-from-top-2 duration-300 flex flex-col">
            {/* Mobile Menu Header - Logo & Close Button */}
            <div className="flex items-center justify-between px-4 sm:px-6 h-16 border-b border-slate-200 bg-[#feffff] shrink-0">
              {/* Logo */}
              <Link href={`/${currentLocale}`} className="flex items-center max-w-[160px]" onClick={() => setMobileMenuOpen(false)}>
                <Image
                  src="/LAKVERSE-black.png"
                  alt="LAKVERSE"
                  width={160}
                  height={53}
                  className="h-8 sm:h-10 w-auto"
                  priority
                  quality={70}
                  sizes="(max-width: 640px) 128px, 160px"
                />
              </Link>
              
              {/* Close Button */}
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 rounded-lg hover:bg-slate-100 transition-colors"
                onClick={() => setMobileMenuOpen(false)}
                aria-label={t.header.menuClose}
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            {/* Mobile Menu Content */}
            <nav className="container mx-auto px-4 sm:px-6 pt-4 pb-4 space-y-3 flex-1 overflow-y-auto">
              {/* Hizmetler Mobile - Accordion */}
              <div className="space-y-1">
                <div className="px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider font-mono">
                  {t.header.services}
                </div>
                {hizmetlerMenu.categories.map((category) => {
                  const isOpen = openCategories.has(category.title)
                  return (
                    <div key={category.title} className="space-y-1">
                      <button
                        onClick={() => toggleCategory(category.title)}
                        className="w-full flex items-center justify-between rounded-lg px-3 py-2.5 text-sm font-semibold text-slate-900 bg-slate-50 hover:bg-slate-100 active:bg-slate-200 transition-all duration-200 touch-manipulation"
                      >
                        <span className="uppercase tracking-wide">{category.title}</span>
                        <ArrowRight 
                          className={`h-4 w-4 text-slate-600 transition-transform duration-200 ${isOpen ? 'rotate-90' : ''}`} 
                        />
                      </button>
                      {isOpen && (
                        <div className="space-y-1 pl-2">
                          {category.items.map((item) => {
                            const Icon = item.icon
                            return (
                              <Link
                                key={item.href}
                                href={`/${currentLocale}${item.href}`}
                                className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 active:bg-slate-100 transition-all duration-200 touch-manipulation"
                                onClick={() => setMobileMenuOpen(false)}
                              >
                                <Icon className="h-4 w-4 text-slate-500 flex-shrink-0" />
                                <span>{item.label}</span>
                              </Link>
                            )
                          })}
                        </div>
                      )}
                    </div>
                  )
                })}
              </div>

              {/* Kurumsal Mobile */}
              <div className="space-y-1">
                <div className="px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider font-mono">
                  {t.header.corporate}
                </div>
                <div className="space-y-1">
                  {kurumsalMenu.items.map((item) => (
                    <Link
                      key={item.href}
                      href={`/${currentLocale}${item.href}`}
                      className="block rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 active:bg-slate-100 transition-all duration-200 touch-manipulation"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              </div>

              {/* Diğer Linkler */}
              <div className="space-y-1">
                <Link
                  href={`/${currentLocale}/${currentLocale === 'tr' ? 'referanslar' : 'references'}`}
                  className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 active:bg-slate-100 transition-all duration-200 touch-manipulation"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Briefcase className="h-4 w-4 text-slate-500 flex-shrink-0" />
                  <span>{t.header.references}</span>
                </Link>

                <Link
                  href={`/${currentLocale}/blog`}
                  className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 active:bg-slate-100 transition-all duration-200 touch-manipulation"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <FileText className="h-4 w-4 text-slate-500 flex-shrink-0" />
                  <span>{t.header.blog}</span>
                </Link>

                <Link
                  href={`/${currentLocale}/iletisim`}
                  className="flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 active:bg-slate-100 transition-all duration-200 touch-manipulation"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Mail className="h-4 w-4 text-slate-500 flex-shrink-0" />
                  <span>{t.header.contact}</span>
                </Link>
              </div>

              {/* Dil Seçici ve Müşteri Paneli */}
              <div className="space-y-1">
                <div className="flex items-center justify-between rounded-lg px-3 py-2 bg-slate-50">
                  <span className="text-sm font-medium text-slate-900">{currentLocale === 'tr' ? 'Dil' : 'Language'}</span>
                  <Select value={currentLocale} onValueChange={(value) => switchLanguage(value as Locale)}>
                    <SelectTrigger 
                      className="h-8 border-0 bg-transparent text-slate-700 px-2 w-auto" 
                      aria-label={currentLocale === 'tr' ? 'Dil seçici: Türkçe' : 'Language selector: English'}
                      suppressHydrationWarning
                    >
                      <SelectValue suppressHydrationWarning>
                        <div className="flex items-center gap-2">
                          <Image
                            src={currentLocale === "tr" ? "/turkey.png" : "/united-states.png"}
                            alt={currentLocale === "tr" ? "Türkçe" : "English"}
                            width={24}
                            height={18}
                            className="w-5 h-5 object-contain"
                          />
                          <span className="text-sm font-medium">{currentLocale === "tr" ? "Türkçe" : "English"}</span>
                        </div>
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent className="z-[70]">
                      <SelectItem value="tr" className="data-[highlighted]:bg-slate-100 data-[highlighted]:text-slate-900">
                        <div className="flex items-center gap-2">
                          <Image
                            src="/turkey.png"
                            alt="Türkçe"
                            width={24}
                            height={18}
                            className="w-5 h-5 object-contain"
                          />
                          <span className="font-medium">Türkçe</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="en" className="data-[highlighted]:bg-slate-100 data-[highlighted]:text-slate-900">
                        <div className="flex items-center gap-2">
                          <Image
                            src="/united-states.png"
                            alt="English"
                            width={24}
                            height={18}
                            className="w-5 h-5 object-contain"
                          />
                          <span className="font-medium">English</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="pt-2 pb-4 space-y-2">
                <Button 
                  asChild
                  className="w-full text-white bg-[#034AA6] hover:bg-[#034AA6]/90 active:bg-[#034AA6]/80 transition-all duration-200 rounded-lg h-11 text-base font-semibold flex items-center justify-center gap-2"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Link href={`/${currentLocale}/hayalini-anlat`}>
                    <Sparkles className="h-4 w-4" />
                    <span>{t.dreamAssistant?.title || (currentLocale === 'tr' ? 'Hayalini Anlat' : 'Tell Your Dream')}</span>
                  </Link>
                </Button>
                
                <Button 
                  asChild 
                  className="w-full text-white bg-slate-900 hover:bg-slate-800 active:bg-slate-950 transition-all duration-200 rounded-lg h-11 text-base font-semibold"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Link href={`/${currentLocale}/${currentLocale === 'tr' ? 'teklif-al' : 'quote-request'}`}>
                    {t.header.getQuote}
                  </Link>
                </Button>
              </div>
            </nav>
          </div>
        </>
      )}
    </header>
  )
}
